import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@ui/tooltip";

import {
  dashboardHeaderColorToRingClassName,
  dashboardHeaderColorToSwatchClassName,
  MS_PER_MINUTE,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import { DashboardHeaderColor, JobPresence } from "@/types";

const PEEP_MAX_MS = 2 * MS_PER_MINUTE;
const CREEPING_MIN_MS = 5 * MS_PER_MINUTE;

const PEEP_PHRASES = [
  "just took a peep",
  "just poked their head in",
  "drive-by",
  "in and out",
  "just a lil look",
];

const CREEPING_PHRASES = [
  "be creeping",
  "posted up",
  "moved in",
  "lurking hard",
  "won't leave",
];

// Deterministic pick so the phrase doesn't flicker between renders, but rolls
// over as the visit ages and differs between teammates.
const pickPhrase = (phrases: string[], userId: number, durationMs: number) => {
  const index =
    (userId + Math.floor(durationMs / MS_PER_MINUTE)) % phrases.length;
  return phrases[index];
};

const formatStay = (durationMs: number, userId: number): string => {
  if (durationMs < PEEP_MAX_MS) {
    return pickPhrase(PEEP_PHRASES, userId, durationMs);
  }
  if (durationMs >= CREEPING_MIN_MS) {
    return `${pickPhrase(CREEPING_PHRASES, userId, durationMs)} · ${dates.formatDuration(durationMs)}`;
  }
  return dates.formatDuration(durationMs);
};

export const JobPresenceAvatars = ({
  presences,
}: {
  presences: JobPresence[];
}) => {
  /* Render */
  // Fixed height whether or not anyone is here, so avatars appearing never
  // reflow the header above them.
  return (
    <TooltipProvider delayDuration={300}>
      <Row className="h-2 items-center gap-3">
        {presences.map((jobPresence) => (
          <JobPresenceAvatar key={jobPresence.user.id} presence={jobPresence} />
        ))}
      </Row>
    </TooltipProvider>
  );
};

const JobPresenceAvatar = ({ presence }: { presence: JobPresence }) => {
  /* Helpers */
  const headerColor =
    presence.user.userSettings?.dashboardHeaderColor ??
    DashboardHeaderColor.LABORUP_ORIGINAL;
  const name = `${presence.user.firstName} ${presence.user.lastName}`;

  /* Render */
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Row
          className={cn(
            "h-6 w-6 items-center justify-center rounded-full ring-2 ring-offset-1",
            dashboardHeaderColorToSwatchClassName[headerColor],
            dashboardHeaderColorToRingClassName[headerColor],
          )}
        >
          <Text className="text-[10px] font-semibold text-white">
            {formatting.initials(
              presence.user.firstName,
              presence.user.lastName,
            )}
          </Text>
        </Row>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <Text className="text-xs font-medium text-slate-700">
          {name} · {formatStay(presence.durationMs, presence.user.id)}
        </Text>
      </TooltipContent>
    </Tooltip>
  );
};
